import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { warehouseService } from '../services/warehouseService';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

// Statystyki raportu: od audytu 2026-09-13 liczone na SERWERZE (jedna definicja:
// partie FIFO x cena zakupu netto, aktywne towary, brutto z VAT produktu, kaucje osobno)
export const PUSTE_STATYSTYKI = {
  totalProducts: 0,
  totalQuantity: 0,
  totalPurchaseValueNet: 0,
  totalPurchaseValueGross: 0,
  totalSaleValueNet: 0,
  totalSaleValueGross: 0,
  stanSztuki: 0,
  wartoscPartiiNet: 0,
  bezPartii: { produkty: 0, sztuki: 0, netto: 0 },
  ujemne: { produkty: 0, sztuki: 0, netto: 0 },
  rozjazd: { produkty: 0, sztuki: 0, netto: 0 },
  kaucjeSzt: 0,
  sklepy: [],
  zSerwera: false,
};

export const statystykiZSerwera = (dane) => {
  const r = dane?.razem || {};
  return {
    totalProducts: r.produkty || 0,
    totalQuantity: r.sztuki || 0,
    totalPurchaseValueNet: (r.wg_stanu && r.wg_stanu.netto != null) ? r.wg_stanu.netto : (r.netto || 0),
    totalPurchaseValueGross: (r.wg_stanu && r.wg_stanu.brutto != null) ? r.wg_stanu.brutto : (r.brutto || 0),
    wartoscPartiiNet: r.netto || 0,
    totalSaleValueNet: r.sprzedaz_netto || 0,
    totalSaleValueGross: r.sprzedaz_brutto || 0,
    stanSztuki: r.stan_sztuki || 0,
    bezPartii: r.bez_partii || PUSTE_STATYSTYKI.bezPartii,
    ujemne: r.ujemne || PUSTE_STATYSTYKI.ujemne,
    rozjazd: r.rozjazd || PUSTE_STATYSTYKI.rozjazd,
    kaucjeSzt: r.kaucje_szt || 0,
    sklepy: dane?.sklepy || [],
    zSerwera: true,
  };
};

// Wartosc netto wiersza: zagregowana (_wartosc_netto) > partie FIFO (fifo_value_netto)
// > szacunek z kartoteki (stan x cena zakupu netto), gdy produkt nie ma partii
export const wartoscNettoWiersza = (p) => {
  if (p._wartosc_netto != null) return parseFloat(p._wartosc_netto) || 0;
  if ((parseFloat(p.fifo_quantity) || 0) > 0) return parseFloat(p.fifo_value_netto) || 0;
  const qty = parseFloat(p.stock_quantity) || 0;
  const cena = parseFloat(p.purchase_price ?? p.cena_zakupu_netto) || 0;
  return qty > 0 ? qty * cena : 0;
};

const InventoryReportPage = () => {
  const { user } = useAuth();
  // Stan dla filtrów
  const [locations, setLocations] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedWarehouses, setSelectedWarehouses] = useState([]);
  // Zbiorczo: ten sam produkt z wielu magazynow w 1 wierszu z suma qty/wartosc
  const [aggregateAcrossWarehouses, setAggregateAcrossWarehouses] = useState(false);
  // Pomijaj produkty z wartoscia netto = 0 (np. cena_zakupu 0,00 lub stan 0)
  const [skipZeroValue, setSkipZeroValue] = useState(false);

  // Wybor kolumn raportu - widoczne w tabeli i eksportach
  const ALL_COLUMNS = [
    { key: 'lp', label: 'Lp.' },
    { key: 'name', label: 'Nazwa produktu' },
    { key: 'barcode', label: 'EAN' },
    { key: 'product_code', label: 'Kod produktu' },
    { key: 'location', label: 'Lokalizacja' },
    { key: 'warehouse', label: 'Magazyn' },
    { key: 'stock', label: 'Stan' },
    { key: 'purchase_net', label: 'Cena zakupu netto' },
    { key: 'purchase_gross', label: 'Cena zakupu brutto' },
    { key: 'value_net', label: 'Wartosc netto' },
    { key: 'sale_net', label: 'Cena sprzedazy netto' },
    { key: 'sale_gross', label: 'Cena sprzedazy brutto' },
  ];
  const [visibleColumns, setVisibleColumns] = useState(
    ALL_COLUMNS.reduce((acc, c) => ({ ...acc, [c.key]: true }), {})
  );
  const [showColumnsMenu, setShowColumnsMenu] = useState(false);
  const isCol = (key) => visibleColumns[key] !== false;

  // Tryb inwentury: dodaje naglowek i podsumowanie do raportu (PDF/CSV/XML)
  const [inventoryMode, setInventoryMode] = useState(false);
  const [inventoryName, setInventoryName] = useState('');
  const [inventoryConductor, setInventoryConductor] = useState('');  // kto przeprowadzil
  const [inventoryCommittee, setInventoryCommittee] = useState('');  // sklad komisji

  // Pre-fill osoby przeprowadzajacej z aktualnie zalogowanego usera
  useEffect(() => {
    if (inventoryMode && !inventoryConductor && user?.login) {
      setInventoryConductor(user.login);
    }
  }, [inventoryMode, inventoryConductor, user]);
  const toggleColumn = (key) => setVisibleColumns(prev => ({ ...prev, [key]: !prev[key] }));
  const setAllColumns = (val) => setVisibleColumns(
    ALL_COLUMNS.reduce((acc, c) => ({ ...acc, [c.key]: val }), {})
  );
  
  // Stan dla filtrów produktów
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    available_only: false
  });
  
  // Stan dla danych - używamy zarówno cen netto jak i brutto
  const [reportData, setReportData] = useState({
    products: [],
    allProducts: [], // Wszystkie produkty do exportu
    locationIds: [], // lokalizacje raportu - do statystyk z serwera (PDF)
    statistics: { ...PUSTE_STATYSTYKI },
    pagination: { page: 1, limit: 50, total: 0, pages: 0 }
  });
  
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Oblicz totalPages na podstawie allProducts
  const totalPages = Math.ceil((reportData.allProducts || []).length / itemsPerPage);

  // Ładowanie lokalizacji przy inicjalizacji
  useEffect(() => {
    loadLocations();
  }, []);

  // Ładowanie magazynów gdy zmienią się wybrane lokalizacje
  useEffect(() => {
    if (selectedLocations.length > 0) {
      loadWarehouses();
    } else {
      setWarehouses([]);
      setSelectedWarehouses([]);
    }
  }, [selectedLocations]);

  // Generowanie raportu gdy zmienią się filtry
  useEffect(() => {
    if (selectedLocations.length > 0 || selectedWarehouses.length > 0) {
      generateReport();
    }
  }, [selectedLocations, selectedWarehouses, filters]);

  // Regeneruj widok gdy zmieni się strona lub ilość na stronę
  useEffect(() => {
    if (reportData.allProducts.length > 0) {
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const paginatedProducts = reportData.allProducts.slice(startIndex, endIndex);
      
      setReportData(prev => ({
        ...prev,
        products: paginatedProducts,
        pagination: {
          ...prev.pagination,
          page: currentPage,
          limit: itemsPerPage,
          pages: Math.ceil(prev.allProducts.length / itemsPerPage)
        }
      }));
    }
  }, [currentPage, itemsPerPage]);

  const loadLocations = async () => {
    try {
      const response = await api.get('/locations/');
      const data = response.data;
      if (data.success) {
        // Poprawka: data.data to już tablica, nie data.data.locations
        const locationsArray = Array.isArray(data.data) ? data.data : (data.data.locations || []);
        setLocations(locationsArray);
      }
    } catch (error) {
      console.error('Błąd ładowania lokalizacji:', error);
      setError('Nie udało się załadować lokalizacji');
    }
  };

  const loadWarehouses = async () => {
    try {
      const response = await api.get('/warehouses');
      const data = response.data;
      if (data.success) {
        // Backend zwraca data jako listę bezpośrednio
        const allWarehouses = Array.isArray(data.data) ? data.data : [];
        // Filtruj magazyny dla wybranych lokalizacji
        const filteredWarehouses = allWarehouses.filter(warehouse =>
          selectedLocations.includes(warehouse.location_id)
        );
        setWarehouses(filteredWarehouses);
      }
    } catch (error) {
      console.error('Błąd ładowania magazynów:', error);
      setError('Nie udało się załadować magazynów');
    }
  };

  const generateReport = async () => {
    setLoading(true);
    setError('');

    try {
      // Jeśli wybrano konkretne magazyny, użyj ich
      // Jeśli nie, użyj wszystkich magazynów z wybranych lokalizacji
      // Jeśli wybrano tylko lokalizacje bez magazynów, użyj location_id
      const warehousesToQuery = selectedWarehouses.length > 0
        ? selectedWarehouses
        : warehouses.map(w => w.id);

      const locationsToQuery = selectedLocations.length > 0 && warehousesToQuery.length === 0
        ? selectedLocations
        : [];

      if (warehousesToQuery.length === 0 && locationsToQuery.length === 0) {
        setReportData({
          products: [],
          allProducts: [],
          locationIds: [],
          statistics: { ...PUSTE_STATYSTYKI },
          pagination: { page: 1, limit: 50, total: 0, pages: 0 }
        });
        return;
      }

      // Pełna lista produktów strona po stronie (limit 1000/strona – bez ucinania stanów ujemnych);
      // tylko aktywne towary – kaucje są liczone osobno w statystykach z serwera
      const allProducts = [];
      const LIMIT = 1000;
      const pobierzWszystkie = async (baseParams, dodatki) => {
        for (let page = 1; page <= 50; page += 1) {
          const result = await warehouseService.getInventory({
            ...baseParams,
            page,
            limit: LIMIT,
            search: filters.search,
            category: filters.category,
            available_only: filters.available_only,
            typ_produktu: 'towar',
            aktywny: '1',
          });
          if (!result.success || !result.data?.products) break;
          result.data.products.forEach(product => allProducts.push({ ...product, ...dodatki }));
          const pages = result.data.pagination?.pages || 1;
          if (page >= pages || result.data.products.length === 0) break;
        }
      };

      const locationIds = [];
      const dodajLokalizacje = (id) => {
        if (id != null && !locationIds.includes(id)) locationIds.push(id);
      };

      // Obsługa zapytań dla magazynów
      for (const warehouseId of warehousesToQuery) {
        const warehouseInfo = warehouses.find(w => w.id === warehouseId);
        const locationInfo = locations.find(l => l.id === warehouseInfo?.location_id);
        dodajLokalizacje(warehouseInfo?.location_id);
        await pobierzWszystkie({ warehouse_id: warehouseId }, {
          warehouse_name: warehouseInfo?.nazwa || 'Nieznany',
          location_name: locationInfo?.nazwa || 'Nieznana',
          warehouse_id: warehouseId,
          location_id: warehouseInfo?.location_id,
        });
      }

      // Obsługa zapytań dla lokalizacji (gdy nie wybrano konkretnych magazynów)
      for (const locationId of locationsToQuery) {
        const locationInfo = locations.find(l => l.id === locationId);
        dodajLokalizacje(locationId);
        await pobierzWszystkie({ location_id: locationId }, {
          warehouse_name: 'Wszystkie magazyny',
          location_name: locationInfo?.nazwa || 'Nieznana',
          warehouse_id: null,
          location_id: locationId,
        });
      }

      // Statystyki liczone na SERWERZE (jedna definicja: partie FIFO x cena zakupu netto)
      let totalStats = { ...PUSTE_STATYSTYKI };
      if (locationIds.length > 0) {
        try {
          const st = await api.get('/reports/products/inventory/statystyki', {
            params: { location_ids: locationIds.join(',') },
          });
          if (st.data?.success && st.data.data?.razem) {
            totalStats = statystykiZSerwera(st.data.data);
          }
        } catch (e) {
          console.error('Błąd statystyk z serwera:', e);
        }
      }
      if (!totalStats.zSerwera) {
        // awaryjnie: suma partii FIFO z wierszy (ta sama definicja, bez linii pomocniczych)
        totalStats.totalProducts = allProducts.filter(p => (parseFloat(p.fifo_quantity) || 0) > 0).length;
        totalStats.totalQuantity = allProducts.reduce((sum, p) => sum + (parseFloat(p.fifo_quantity) || 0), 0);
        totalStats.totalPurchaseValueNet = allProducts.reduce((sum, p) => sum + (parseFloat(p.fifo_value_netto) || 0), 0);
        setError('Statystyki z serwera niedostępne – pokazano sumę partii FIFO z listy (bez linii pomocniczych)');
      }

      // Paginacja produktów
      const startIndex = (currentPage - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      const paginatedProducts = allProducts.slice(startIndex, endIndex);

      setReportData({
        products: paginatedProducts,
        allProducts: allProducts, // Wszystkie produkty do exportu
        locationIds,
        statistics: totalStats,
        pagination: { 
          page: currentPage, 
          limit: itemsPerPage, 
          total: allProducts.length, 
          pages: Math.ceil(allProducts.length / itemsPerPage) 
        }
      });

    } catch (error) {
      console.error('Błąd generowania raportu:', error);
      setError('Nie udało się wygenerować raportu');
    } finally {
      setLoading(false);
    }
  };

  const handleLocationChange = (locationId, checked) => {
    if (checked) {
      setSelectedLocations([...selectedLocations, locationId]);
    } else {
      setSelectedLocations(selectedLocations.filter(id => id !== locationId));
      // Usuń też magazyny z tej lokalizacji
      const warehousesToRemove = warehouses
        .filter(w => w.location_id === locationId)
        .map(w => w.id);
      setSelectedWarehouses(selectedWarehouses.filter(id => !warehousesToRemove.includes(id)));
    }
  };

  const handleWarehouseChange = (warehouseId, checked) => {
    if (checked) {
      setSelectedWarehouses([...selectedWarehouses, warehouseId]);
    } else {
      setSelectedWarehouses(selectedWarehouses.filter(id => id !== warehouseId));
    }
  };

  /**
   * Agreguj liste produktow: ten sam produkt z roznych magazynow -> 1 wiersz.
   * Sumujemy stock_quantity i wartosc netto. Magazyny lista nazw.
   */
  const aggregateProducts = (products) => {
    const map = new Map();
    for (const p of products) {
      // Klucz: id (jesli jest) > barcode > product_code > nazwa
      const key = p.id ?? p.barcode ?? p.product_code ?? p.name;
      if (key == null || key === '') continue;
      const qty = parseFloat(p.stock_quantity) || 0;
      const purchaseNet = parseFloat(p.purchase_price ?? p.cena_zakupu_netto) || 0;
      const purchaseGross = parseFloat(p.cena_zakupu_brutto) || 0;
      const saleNet = parseFloat(p.price_net ?? p.cena_sprzedazy_netto) || 0;
      const saleGross = parseFloat(p.price ?? p.cena_sprzedazy_brutto) || 0;
      const wartoscNetto = wartoscNettoWiersza(p);

      if (map.has(key)) {
        const acc = map.get(key);
        acc.stock_quantity += qty;
        acc._wartosc_netto += wartoscNetto;
        // Zlistuj magazyny (unikat)
        if (p.warehouse_name && !acc._warehouses.has(p.warehouse_name)) {
          acc._warehouses.add(p.warehouse_name);
        }
        if (p.location_name && !acc._locations.has(p.location_name)) {
          acc._locations.add(p.location_name);
        }
      } else {
        map.set(key, {
          ...p,
          stock_quantity: qty,
          purchase_price: purchaseNet,
          cena_zakupu_netto: purchaseNet,
          cena_zakupu_brutto: purchaseGross,
          price_net: saleNet,
          cena_sprzedazy_netto: saleNet,
          price: saleGross,
          cena_sprzedazy_brutto: saleGross,
          _wartosc_netto: wartoscNetto,
          _warehouses: new Set(p.warehouse_name ? [p.warehouse_name] : []),
          _locations: new Set(p.location_name ? [p.location_name] : []),
          warehouse_id: null,
          location_id: null,
        });
      }
    }
    return Array.from(map.values()).map(p => ({
      ...p,
      warehouse_name: p._warehouses.size > 1
        ? `${p._warehouses.size} magazynow`
        : (Array.from(p._warehouses)[0] || ''),
      location_name: p._locations.size > 1
        ? `${p._locations.size} lokalizacji`
        : (Array.from(p._locations)[0] || ''),
    }));
  };

  // Lista produktow do exportu - zagregowana lub pelna, opcjonalnie filtrowana
  const exportProducts = () => {
    let list = aggregateAcrossWarehouses
      ? aggregateProducts(reportData.allProducts || [])
      : (reportData.allProducts || []);
    if (skipZeroValue) {
      list = list.filter(p => Math.round(wartoscNettoWiersza(p) * 100) > 0);  // > 0,00
    }
    return list;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('pl-PL', {
      style: 'currency',
      currency: 'PLN'
    }).format(amount || 0);
  };

  // Polski format liczb: kropka -> przecinek (np. 49.99 -> 49,99)
  const pl = (n) => (Number(n) || 0).toFixed(2).replace('.', ',');

  // Funkcja eksportu do CSV/Excel
  const exportToCSV = () => {
    const productsToExport = exportProducts();
    // Zbuduj naglowki tylko dla widocznych kolumn
    const colDefs = [
      { key: 'lp', label: 'Lp.', val: (_, i) => String(i + 1) },
      { key: 'name', label: 'Nazwa produktu', val: p => `"${(p.name || '').replace(/"/g, '""')}"` },
      { key: 'barcode', label: 'EAN', val: p => `"${p.barcode || ''}"` },
      { key: 'product_code', label: 'Kod produktu', val: p => `"${p.product_code || ''}"` },
      { key: 'location', label: 'Lokalizacja', val: p => `"${p.location_name || ''}"` },
      { key: 'warehouse', label: 'Magazyn', val: p => `"${p.warehouse_name || ''}"` },
      { key: 'stock', label: 'Stan', val: p => String(Math.round(p.stock_quantity || 0)) },
      { key: 'purchase_net', label: 'Cena zakupu netto',
        val: p => pl(p.purchase_price || p.cena_zakupu_netto || 0) },
      { key: 'purchase_gross', label: 'Cena zakupu brutto',
        val: p => pl(p.cena_zakupu_brutto || 0) },
      { key: 'value_net', label: 'Wartosc netto',
        val: p => pl(wartoscNettoWiersza(p)) },
      { key: 'sale_net', label: 'Cena sprzedazy netto',
        val: p => pl(p.price_net || p.cena_sprzedazy_netto || 0) },
      { key: 'sale_gross', label: 'Cena sprzedazy brutto',
        val: p => pl(p.price || p.cena_sprzedazy_brutto || 0) },
    ].filter(c => isCol(c.key));

    const lines = [];
    if (inventoryMode) {
      lines.push(`# INWENTURA: ${inventoryName || '-'}`);
      lines.push(`# Przeprowadzajacy: ${inventoryConductor || '-'}`);
      if (inventoryCommittee) lines.push(`# Sklad: ${inventoryCommittee}`);
      lines.push(`# Inwentura zakonczyla sie na pozycji ${productsToExport.length}`);
      lines.push('');  // pusta linia separator
    }
    lines.push(colDefs.map(c => c.label).join(';'));
    productsToExport.forEach((product, index) => {
      lines.push(colDefs.map(c => c.val(product, index)).join(';'));
    });
    const csvContent = lines.join('\n');

    const blob = new Blob(['﻿' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `raport_magazynowy_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // Funkcja eksportu do XML
  const exportToXML = () => {
    const productsToExport = exportProducts();
    let xml = '<?xml version="1.0" encoding="UTF-8"?>\n';
    xml += '<RaportMagazynowy>\n';
    xml += `  <DataGenerowania>${new Date().toISOString()}</DataGenerowania>\n`;
    if (inventoryMode) {
      xml += '  <Inwentura>\n';
      xml += `    <Nazwa><![CDATA[${inventoryName || ''}]]></Nazwa>\n`;
      xml += `    <Przeprowadzajacy><![CDATA[${inventoryConductor || ''}]]></Przeprowadzajacy>\n`;
      xml += `    <Sklad><![CDATA[${inventoryCommittee || ''}]]></Sklad>\n`;
      xml += `    <Podsumowanie><![CDATA[Inwentura zakończyła się na pozycji ${productsToExport.length}]]></Podsumowanie>\n`;
      xml += `    <OstatniaPozycja>${productsToExport.length}</OstatniaPozycja>\n`;
      xml += '  </Inwentura>\n';
    }
    const st = reportData.statistics;
    xml += '  <Statystyki definicja="partie FIFO x cena zakupu netto; aktywne towary; kaucje osobno">\n';
    xml += `    <LiczbaProduktow>${st.totalProducts}</LiczbaProduktow>\n`;
    xml += `    <LacznaIlosc>${Math.round(st.totalQuantity)}</LacznaIlosc>\n`;
    xml += `    <StanLacznie>${Math.round(st.stanSztuki || 0)}</StanLacznie>\n`;
    xml += `    <WartoscZakupuNetto>${pl(st.totalPurchaseValueNet)}</WartoscZakupuNetto>\n`;
    xml += `    <WartoscZakupuBrutto>${pl(st.totalPurchaseValueGross)}</WartoscZakupuBrutto>\n`;
    xml += `    <BezPartiiSzacunek produkty="${st.bezPartii?.produkty || 0}" sztuki="${Math.round(st.bezPartii?.sztuki || 0)}">${pl(st.bezPartii?.netto)}</BezPartiiSzacunek>\n`;
    xml += `    <StanyUjemne produkty="${st.ujemne?.produkty || 0}" sztuki="${Math.round(st.ujemne?.sztuki || 0)}">${pl(st.ujemne?.netto)}</StanyUjemne>\n`;
    xml += `    <RoznicaStanPartie produkty="${st.rozjazd?.produkty || 0}" sztuki="${Math.round(st.rozjazd?.sztuki || 0)}">${pl(st.rozjazd?.netto)}</RoznicaStanPartie>\n`;
    xml += `    <KaucjeSztuk>${Math.round(st.kaucjeSzt || 0)}</KaucjeSztuk>\n`;
    xml += '  </Statystyki>\n';
    xml += '  <Produkty>\n';

    productsToExport.forEach((product, index) => {
      const wartoscNetto = wartoscNettoWiersza(product);
      xml += '    <Produkt>\n';
      if (isCol('lp')) xml += `      <Lp>${index + 1}</Lp>\n`;
      if (isCol('name')) xml += `      <Nazwa><![CDATA[${product.name || ''}]]></Nazwa>\n`;
      if (isCol('barcode')) xml += `      <EAN><![CDATA[${product.barcode || ''}]]></EAN>\n`;
      if (isCol('product_code')) xml += `      <KodProduktu><![CDATA[${product.product_code || ''}]]></KodProduktu>\n`;
      if (isCol('location')) xml += `      <Lokalizacja><![CDATA[${product.location_name || ''}]]></Lokalizacja>\n`;
      if (isCol('warehouse')) xml += `      <Magazyn><![CDATA[${product.warehouse_name || ''}]]></Magazyn>\n`;
      if (isCol('stock')) xml += `      <Stan>${Math.round(product.stock_quantity || 0)}</Stan>\n`;
      if (isCol('purchase_net')) xml += `      <CenaZakupuNetto>${pl(product.purchase_price || product.cena_zakupu_netto || 0)}</CenaZakupuNetto>\n`;
      if (isCol('purchase_gross')) xml += `      <CenaZakupuBrutto>${pl(product.cena_zakupu_brutto || 0)}</CenaZakupuBrutto>\n`;
      if (isCol('value_net')) xml += `      <WartoscNetto>${pl(wartoscNetto)}</WartoscNetto>\n`;
      if (isCol('sale_net')) xml += `      <CenaSprzedazyNetto>${pl(product.price_net || product.cena_sprzedazy_netto || 0)}</CenaSprzedazyNetto>\n`;
      if (isCol('sale_gross')) xml += `      <CenaSprzedazyBrutto>${pl(product.price || product.cena_sprzedazy_brutto || 0)}</CenaSprzedazyBrutto>\n`;
      xml += '    </Produkt>\n';
    });
    
    xml += '  </Produkty>\n';
    xml += '</RaportMagazynowy>';

    const blob = new Blob([xml], { type: 'application/xml' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `raport_magazynowy_${new Date().toISOString().split('T')[0]}.xml`;
    link.click();
  };

  // Funkcja eksportu do PDF (backend ReportLab)
  const exportToPDF = async () => {
    try {
      const productsForPdf = exportProducts();
      const response = await api.post('/reports/products/inventory/pdf', {
        products: productsForPdf,
        statistics: reportData.statistics,
        // statystyki PDF liczy serwer (ta sama definicja co kafelki), nie przeglądarka
        location_ids: reportData.locationIds || [],
        inventory: inventoryMode ? {
          name: inventoryName || '',
          conductor: inventoryConductor || '',
          committee: inventoryCommittee || '',
          summary: `Inwentura zakończyła się na pozycji ${productsForPdf.length}`,
          last_position: productsForPdf.length,
        } : null,
      }, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch {
      toast.error('Blad generowania PDF raportu magazynowego.');
    }
  };

  return (
    <div style={{ padding: '0.75rem', maxWidth: '1600px', margin: '0 auto' }}>
      {/* Naglowek */}
      <div className="card" style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
          <div>
            <h2 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '4px' }}>Raport Magazynowy</h2>
            <p style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Przegladaj stany magazynowe i eksportuj raporty</p>
          </div>
          <div style={{ display: 'flex', gap: '8px', position: 'relative', flexWrap: 'wrap' }}>
            <button
              className="btn btn-secondary"
              onClick={() => setShowColumnsMenu(!showColumnsMenu)}
              title="Wybierz ktore kolumny pokazac w tabeli i eksportach"
            >
              📊 Kolumny ({Object.values(visibleColumns).filter(Boolean).length}/{ALL_COLUMNS.length})
            </button>
            {showColumnsMenu && (
              <div style={{
                position: 'absolute', top: '100%', right: 0, marginTop: '4px',
                backgroundColor: 'var(--card-bg)', border: '1px solid var(--border)',
                borderRadius: '6px', padding: '0.75rem', minWidth: '240px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 100,
              }}>
                <div style={{ display: 'flex', gap: '4px', marginBottom: '0.5rem',
                              borderBottom: '1px solid var(--border)', paddingBottom: '0.4rem' }}>
                  <button onClick={() => setAllColumns(true)}
                    style={{ flex: 1, padding: '4px 10px', fontSize: '11px', cursor: 'pointer',
                             border: '1px solid var(--border)',
                             backgroundColor: 'var(--bg)' }}>Wszystkie</button>
                  <button onClick={() => setAllColumns(false)}
                    style={{ flex: 1, padding: '4px 10px', fontSize: '11px', cursor: 'pointer',
                             border: '1px solid var(--border)',
                             backgroundColor: 'var(--bg)' }}>Zadne</button>
                </div>
                {ALL_COLUMNS.map(col => (
                  <label key={col.key} style={{
                    display: 'flex', alignItems: 'center', cursor: 'pointer',
                    fontSize: '0.75rem', padding: '0.2rem 0',
                  }}>
                    <input type="checkbox"
                      checked={!!visibleColumns[col.key]}
                      onChange={() => toggleColumn(col.key)}
                      style={{ marginRight: '6px' }}
                    />
                    {col.label}
                  </label>
                ))}
                <button onClick={() => setShowColumnsMenu(false)}
                  style={{ marginTop: '0.5rem', width: '100%', padding: '4px 10px',
                           fontSize: '11px', cursor: 'pointer',
                           border: '1px solid var(--border)',
                           backgroundColor: 'var(--info)', color: '#fff' }}>Zamknij</button>
              </div>
            )}
            <button
              className="btn btn-primary"
              onClick={exportToCSV}
              disabled={reportData.allProducts.length === 0}
            >
              CSV
            </button>
            <button
              className="btn btn-secondary"
              onClick={exportToXML}
              disabled={reportData.allProducts.length === 0}
            >
              XML
            </button>
            <button
              className="btn btn-secondary"
              onClick={exportToPDF}
              disabled={reportData.allProducts.length === 0}
            >
              PDF
            </button>
          </div>
        </div>
      </div>

      {/* Filtry - kompaktowy styl */}
      <div style={{ 
        backgroundColor: 'var(--card-bg)', 
        border: '1px solid var(--border)',
        borderRadius: '4px',
        padding: '0.5rem 0.75rem',
        marginBottom: '0.5rem'
      }}>
        <h3 style={{ marginTop: 0, color: 'var(--text-secondary)', marginBottom: '0.5rem', fontSize: '0.8rem', fontWeight: '600' }}>
          🔍 Filtry
        </h3>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.5rem', marginBottom: '0.5rem' }}>
          {/* Wybór lokalizacji */}
          <div>
            <h4 style={{ marginBottom: '0.25rem', color: 'var(--text-muted)', fontSize: '0.7rem' }}>📍 Lokalizacje</h4>
            <div style={{ 
              maxHeight: '80px', 
              overflowY: 'auto',
              border: '1px solid var(--border)',
              borderRadius: '4px',
              padding: '0.35rem',
              backgroundColor: 'var(--card-bg)',
              fontSize: '0.7rem'
            }}>
              {locations.map(location => (
                <label key={location.id} style={{ display: 'block', marginBottom: '2px', cursor: 'pointer', fontSize: '0.7rem' }}>
                  <input
                    type="checkbox"
                    checked={selectedLocations.includes(location.id)}
                    onChange={(e) => handleLocationChange(location.id, e.target.checked)}
                    style={{ marginRight: '4px' }}
                  />
                  {location.nazwa}
                </label>
              ))}
            </div>
          </div>

          {/* Wybór magazynów */}
          <div>
            <h4 style={{ marginBottom: '0.25rem', color: 'var(--text-muted)', fontSize: '0.7rem' }}>🏪 Magazyny</h4>
            <div style={{ 
              maxHeight: '80px', 
              overflowY: 'auto', 
              border: '1px solid var(--border)',
              borderRadius: '4px',
              padding: '0.35rem',
              backgroundColor: 'var(--card-bg)',
              fontSize: '0.7rem'
            }}>
              {warehouses.length === 0 ? (
                <p style={{ color: 'var(--text-muted)', fontStyle: 'italic', margin: 0, fontSize: '0.65rem' }}>
                  Wybierz lokalizację
                </p>
              ) : (
                warehouses.map(warehouse => (
                  <label key={warehouse.id} style={{ display: 'block', marginBottom: '2px', cursor: 'pointer', fontSize: '0.7rem' }}>
                    <input
                      type="checkbox"
                      checked={selectedWarehouses.includes(warehouse.id)}
                      onChange={(e) => handleWarehouseChange(warehouse.id, e.target.checked)}
                      style={{ marginRight: '4px' }}
                    />
                    {warehouse.nazwa} ({warehouse.kod_magazynu})
                  </label>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Filtry produktów */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.5rem' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '2px', fontWeight: '600', fontSize: '11px', color: 'var(--text-muted)' }}>🔎 Szukaj</label>
            <input
              type="text"
              value={filters.search}
              onChange={(e) => setFilters({...filters, search: e.target.value})}
              placeholder="Nazwa, kod, EAN..."
              style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '12px', backgroundColor: 'var(--card-bg)', color: 'var(--text)' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '2px', fontWeight: '600', fontSize: '11px', color: 'var(--text-muted)' }}>📂 Kategoria</label>
            <input
              type="text"
              value={filters.category}
              onChange={(e) => setFilters({...filters, category: e.target.value})}
              placeholder="Kategoria..."
              style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--border)', borderRadius: '6px', fontSize: '12px', backgroundColor: 'var(--card-bg)', color: 'var(--text)' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '2px', fontWeight: '600', fontSize: '0.65rem' }}>✅ Opcje</label>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '0.7rem' }}>
              <input
                type="checkbox"
                checked={filters.available_only}
                onChange={(e) => setFilters({...filters, available_only: e.target.checked})}
                style={{ marginRight: '4px' }}
              />
              Tylko dostępne
            </label>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '0.7rem', marginTop: '2px' }}
                   title="Sumuj ten sam produkt z roznych magazynow do jednego wiersza (stan + wartosc netto)">
              <input
                type="checkbox"
                checked={aggregateAcrossWarehouses}
                onChange={(e) => setAggregateAcrossWarehouses(e.target.checked)}
                style={{ marginRight: '4px' }}
              />
              Zbiorczo (sumuj magazyny)
            </label>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '0.7rem', marginTop: '2px' }}
                   title="Dodaj naglowek i podsumowanie inwentury (nazwa, sklad komisji, podsumowanie)">
              <input
                type="checkbox"
                checked={inventoryMode}
                onChange={(e) => setInventoryMode(e.target.checked)}
                style={{ marginRight: '4px' }}
              />
              📋 Inwentura
            </label>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '0.7rem', marginTop: '2px' }}
                   title="Pomijaj produkty z wartoscia netto 0,00 (np. brak ceny zakupu lub stanu)">
              <input
                type="checkbox"
                checked={skipZeroValue}
                onChange={(e) => setSkipZeroValue(e.target.checked)}
                style={{ marginRight: '4px' }}
              />
              Pomijaj 0,00
            </label>
          </div>
        </div>
      </div>

      {/* Inwentura - dodatkowe pola (widoczne gdy checkbox 'Inwentura' zaznaczony) */}
      {inventoryMode && (
        <div style={{
          backgroundColor: 'var(--info-light, #e3f2fd)',
          border: '1px solid var(--info, #2196f3)',
          borderRadius: '4px',
          padding: '0.75rem 1rem',
          marginBottom: '0.5rem',
        }}>
          <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.85rem', fontWeight: '600' }}>
            📋 Dane inwentury
          </h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.5rem' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: '600', marginBottom: '2px' }}>
                Nazwa inwentury
              </label>
              <input
                type="text"
                value={inventoryName}
                onChange={(e) => setInventoryName(e.target.value)}
                placeholder="np. Inwentura roczna 2026 - SFD Wejherowo"
                style={{ width: '100%', padding: '0.3rem 0.5rem', fontSize: '0.75rem',
                         border: '1px solid var(--border)', borderRadius: '3px' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: '600', marginBottom: '2px' }}>
                Przeprowadzający
              </label>
              <input
                type="text"
                value={inventoryConductor}
                onChange={(e) => setInventoryConductor(e.target.value)}
                placeholder="Imie i nazwisko"
                style={{ width: '100%', padding: '0.3rem 0.5rem', fontSize: '0.75rem',
                         border: '1px solid var(--border)', borderRadius: '3px' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: '600', marginBottom: '2px' }}>
                Skład komisji (opcjonalnie)
              </label>
              <input
                type="text"
                value={inventoryCommittee}
                onChange={(e) => setInventoryCommittee(e.target.value)}
                placeholder="np. Robert Kaczkowski, Jan Kowalski"
                style={{ width: '100%', padding: '0.3rem 0.5rem', fontSize: '0.75rem',
                         border: '1px solid var(--border)', borderRadius: '3px' }}
              />
            </div>
          </div>
          <div style={{ marginTop: '0.4rem', fontSize: '0.7rem', color: 'var(--text-muted)' }}>
            Auto-generowane: <em>"Inwentura zakończyła się na pozycji {(reportData.allProducts || []).length}"</em>
          </div>
        </div>
      )}

      {/* Statystyki - liczone na serwerze (partie FIFO x cena zakupu netto) */}
      {!loading && (selectedLocations.length > 0 || selectedWarehouses.length > 0) && (
        <div style={{ marginBottom: '0.5rem' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '0.5rem',
            marginBottom: '0.35rem'
          }}>
            {[
              { kolor: 'info', etykieta: 'Produktów z partiami FIFO', wartosc: reportData.statistics.totalProducts,
                opis: 'Aktywne towary, które mają partie z ilością pozostałą > 0', testId: 'stat-produkty' },
              { kolor: 'purple', etykieta: `Sztuk w partiach (stan łącznie: ${Math.round(reportData.statistics.stanSztuki || 0)})`,
                wartosc: Math.round(reportData.statistics.totalQuantity), opis: 'Suma ilości pozostałej w partiach FIFO; w nawiasie suma stanów magazynowych', testId: 'stat-sztuki' },
              { kolor: 'success', etykieta: 'Wartość magazynu netto (FIFO)', wartosc: formatCurrency(reportData.statistics.totalPurchaseValueNet),
                opis: 'SUM(ilość pozostała × cena zakupu netto) z partii FIFO; aktywne towary, bez kaucji', testId: 'stat-netto' },
              { kolor: 'warning', etykieta: 'Wartość brutto (VAT produktu)', wartosc: formatCurrency(reportData.statistics.totalPurchaseValueGross),
                opis: 'netto × (1 + stawka VAT produktu); stawka ujemna (zw) = 0%', testId: 'stat-brutto' },
              { kolor: 'info', etykieta: 'Wartość sprzedaży brutto', wartosc: formatCurrency(reportData.statistics.totalSaleValueGross),
                opis: 'Stan × cena sprzedaży brutto (dodatnie stany) – to nie jest wartość magazynu', testId: 'stat-sprzedaz' },
            ].map((k) => (
              <div key={k.testId} data-testid={k.testId} title={k.opis} style={{
                backgroundColor: 'var(--card-bg)',
                padding: '0.6rem 0.75rem',
                borderRadius: '6px',
                textAlign: 'center',
                border: '1px solid var(--border)',
                borderLeft: `4px solid var(--${k.kolor})`
              }}>
                <h4 style={{ margin: '0 0 4px 0', color: `var(--${k.kolor})`, fontSize: '0.7rem' }}>{k.etykieta}</h4>
                <p style={{ fontSize: '1rem', fontWeight: 'bold', margin: 0, color: `var(--${k.kolor})` }}>{k.wartosc}</p>
              </div>
            ))}
          </div>
          {reportData.statistics.zSerwera && (
            <div data-testid="stat-poza-wartoscia" style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', border: '1px dashed var(--border)', borderRadius: '6px', padding: '0.4rem 0.75rem', backgroundColor: 'var(--card-bg)' }}>
              <strong>Poza wartością główną (nie wliczane):</strong>{' '}
              stan bez partii FIFO – szacunek z kartoteki: <strong>{reportData.statistics.bezPartii.produkty}</strong> prod. / {Math.round(reportData.statistics.bezPartii.sztuki)} szt / <strong>{formatCurrency(reportData.statistics.bezPartii.netto)}</strong>
              {' · '}stany ujemne (sprzedane przed dostawą): <strong>{reportData.statistics.ujemne.produkty}</strong> prod. / {Math.round(reportData.statistics.ujemne.sztuki)} szt / <strong>{formatCurrency(reportData.statistics.ujemne.netto)}</strong>
              {' · '}różnica stan − partie: <strong>{reportData.statistics.rozjazd.produkty}</strong> prod. / {Math.round(reportData.statistics.rozjazd.sztuki)} szt / <strong>{formatCurrency(reportData.statistics.rozjazd.netto)}</strong>
              {' · '}opakowania/kaucje: <strong>{Math.round(reportData.statistics.kaucjeSzt)}</strong> szt
              {reportData.statistics.sklepy.length > 1 && (
                <span> · sklepy: {reportData.statistics.sklepy.map(sk => `${sk.nazwa} ${formatCurrency(sk.netto)}`).join(', ')}</span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Kontrolki paginacji */}
      {!loading && reportData.allProducts.length > 0 && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '0.5rem',
          padding: '0.35rem 0.5rem',
          backgroundColor: 'var(--card-bg)',
          borderRadius: '4px',
          border: '1px solid var(--border)',
          fontSize: '0.7rem',
          flexWrap: 'wrap',
          gap: '0.5rem'
        }}>
          <label style={{ fontWeight: '500' }}>
            Na stronę:
            <select 
              value={itemsPerPage} 
              onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
              style={{ marginLeft: '4px', padding: '6px 10px', fontSize: '12px', border: '1px solid var(--border)', borderRadius: '6px', backgroundColor: 'var(--card-bg)', color: 'var(--text)' }}
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={500}>500</option>
            </select>
          </label>
          <span style={{ color: 'var(--text-muted)' }}>
            Strona {currentPage} z {totalPages} ({reportData.allProducts.length} produktów)
          </span>
        </div>
      )}

      {/* Stan ładowania */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '1.5rem', color: 'var(--text-muted)' }}>
          <p style={{ fontSize: '0.8rem' }}>⏳ Generowanie raportu...</p>
        </div>
      )}

      {/* Błędy */}
      {error && (
        <div style={{ 
          backgroundColor: 'var(--danger-light)', 
          color: 'var(--danger)',
          padding: '0.5rem 0.75rem',
          borderRadius: '4px',
          marginBottom: '0.5rem',
          fontSize: '0.75rem'
        }}>
          ❌ {error}
        </div>
      )}

      {/* Tabela produktów */}
      {!loading && reportData.products.length > 0 && (
        <div style={{ 
          backgroundColor: 'var(--card-bg)',
          border: '1px solid var(--border)',
          borderRadius: '4px',
          overflow: 'hidden'
        }}>
          <h3 style={{ 
            margin: 0, 
            padding: '0.4rem 0.75rem',
            backgroundColor: 'var(--bg)',
            borderBottom: '1px solid var(--border)',
            color: 'var(--text-secondary)',
            fontSize: '0.75rem',
            fontWeight: '600'
          }}>
            📋 Produkty ({reportData.pagination.total})
          </h3>
          
          <div className="admin-table-wrap" style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.65rem' }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--bg)' }}>
                  {isCol('lp') && <th style={{ padding: '0.3rem', textAlign: 'center', borderBottom: '1px solid var(--border)', width: '30px', fontSize: '0.6rem' }}>Lp.</th>}
                  {(isCol('name') || isCol('barcode') || isCol('product_code')) && <th style={{ padding: '0.3rem', textAlign: 'left', borderBottom: '1px solid var(--border)', fontSize: '0.6rem' }}>Produkt</th>}
                  {isCol('location') && <th className="col-hide-mobile" style={{ padding: '0.3rem', textAlign: 'left', borderBottom: '1px solid var(--border)', fontSize: '0.6rem' }}>Lokalizacja</th>}
                  {isCol('warehouse') && <th className="col-hide-mobile" style={{ padding: '0.3rem', textAlign: 'left', borderBottom: '1px solid var(--border)', fontSize: '0.6rem' }}>Magazyn</th>}
                  {isCol('stock') && <th style={{ padding: '0.3rem', textAlign: 'center', borderBottom: '1px solid var(--border)', fontSize: '0.6rem', whiteSpace: 'nowrap' }}>Stan</th>}
                  {isCol('purchase_net') && <th className="col-hide-mobile" style={{ padding: '0.3rem', textAlign: 'right', borderBottom: '1px solid var(--border)', fontSize: '0.6rem', whiteSpace: 'nowrap' }}>Zakup netto</th>}
                  {isCol('purchase_gross') && <th className="col-hide-mobile" style={{ padding: '0.3rem', textAlign: 'right', borderBottom: '1px solid var(--border)', fontSize: '0.6rem', whiteSpace: 'nowrap' }}>Zakup brutto</th>}
                  {isCol('value_net') && <th style={{ padding: '0.3rem', textAlign: 'right', borderBottom: '1px solid var(--border)', fontSize: '0.6rem', whiteSpace: 'nowrap' }}>Wartość netto</th>}
                  {isCol('sale_net') && <th className="col-hide-mobile" style={{ padding: '0.3rem', textAlign: 'right', borderBottom: '1px solid var(--border)', fontSize: '0.6rem', whiteSpace: 'nowrap' }}>Sprzedaż netto</th>}
                  {isCol('sale_gross') && <th style={{ padding: '0.3rem', textAlign: 'right', borderBottom: '1px solid var(--border)', fontSize: '0.6rem', whiteSpace: 'nowrap' }}>Sprzedaż brutto</th>}
                </tr>
              </thead>
              <tbody>
                {reportData.products.map((product, index) => (
                  <tr key={`${product.id}-${product.warehouse_id}`} style={{
                    backgroundColor: index % 2 === 0 ? 'var(--card-bg)' : 'var(--bg)'
                  }}>
                    {isCol('lp') && (
                      <td style={{ padding: '0.25rem', borderBottom: '1px solid var(--bg)', textAlign: 'center', fontWeight: 'bold', color: 'var(--text-secondary)' }}>
                        {(currentPage - 1) * itemsPerPage + index + 1}
                      </td>
                    )}
                    {(isCol('name') || isCol('barcode') || isCol('product_code')) && (
                      <td style={{ padding: '0.25rem 0.35rem', borderBottom: '1px solid var(--bg)' }}>
                        {isCol('name') && <strong style={{ fontSize: '0.65rem' }}>{product.name}</strong>}
                        {(isCol('barcode') || isCol('product_code')) && (
                          <>
                            <br />
                            <small style={{ color: 'var(--text-muted)', fontSize: '0.55rem' }}>
                              {isCol('barcode') && product.barcode && `EAN: ${product.barcode}`}
                              {isCol('product_code') && product.product_code && (isCol('barcode') && product.barcode ? ` | ${product.product_code}` : product.product_code)}
                            </small>
                          </>
                        )}
                      </td>
                    )}
                    {isCol('location') && <td className="col-hide-mobile" style={{ padding: '0.25rem 0.35rem', borderBottom: '1px solid var(--bg)' }}>{product.location_name}</td>}
                    {isCol('warehouse') && <td className="col-hide-mobile" style={{ padding: '0.25rem 0.35rem', borderBottom: '1px solid var(--bg)' }}>{product.warehouse_name}</td>}
                    {isCol('stock') && (
                      <td style={{ padding: '0.25rem', borderBottom: '1px solid var(--bg)', textAlign: 'center', fontWeight: 'bold',
                                   color: product.stock_quantity > 0 ? 'var(--success)' : 'var(--danger)', whiteSpace: 'nowrap' }}>
                        {Math.round(product.stock_quantity || 0)}
                      </td>
                    )}
                    {isCol('purchase_net') && (
                      <td className="col-hide-mobile" style={{ padding: '0.25rem 0.35rem', borderBottom: '1px solid var(--bg)', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {formatCurrency(product.purchase_price || product.cena_zakupu_netto || 0)}
                      </td>
                    )}
                    {isCol('purchase_gross') && (
                      <td className="col-hide-mobile" style={{ padding: '0.25rem 0.35rem', borderBottom: '1px solid var(--bg)', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {formatCurrency(product.cena_zakupu_brutto || 0)}
                      </td>
                    )}
                    {isCol('value_net') && (
                      <td style={{ padding: '0.25rem 0.35rem', borderBottom: '1px solid var(--bg)', textAlign: 'right', fontWeight: 'bold', whiteSpace: 'nowrap' }}
                          title={(parseFloat(product.fifo_quantity) || 0) > 0 ? `Partie FIFO: ${Math.round(product.fifo_quantity)} szt` : 'Brak partii FIFO – szacunek z kartoteki'}>
                        {formatCurrency(wartoscNettoWiersza(product))}
                        {(parseFloat(product.fifo_quantity) || 0) <= 0 && (parseFloat(product.stock_quantity) || 0) > 0 && (
                          <span style={{ fontSize: '0.55rem', color: 'var(--orange)', marginLeft: '3px' }}>szac.</span>
                        )}
                      </td>
                    )}
                    {isCol('sale_net') && (
                      <td className="col-hide-mobile" style={{ padding: '0.25rem 0.35rem', borderBottom: '1px solid var(--bg)', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {formatCurrency(product.price_net || product.cena_sprzedazy_netto || 0)}
                      </td>
                    )}
                    {isCol('sale_gross') && (
                      <td style={{ padding: '0.25rem 0.35rem', borderBottom: '1px solid var(--bg)', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {formatCurrency(product.price || product.cena_sprzedazy_brutto || 0)}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            
            {/* Kontrolki paginacji */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: '0.35rem',
              padding: '0.35rem 0.5rem',
              backgroundColor: 'var(--bg)',
              borderTop: '1px solid var(--border)',
              fontSize: '0.65rem',
              flexWrap: 'wrap',
              gap: '0.5rem'
            }}>
              <div style={{ color: 'var(--text-muted)' }}>
                Strona {currentPage} z {totalPages} ({reportData.allProducts.length} produktów)
              </div>
              
              <div style={{ display: 'flex', gap: '0.25rem' }}>
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  style={{
                    padding: '4px 10px',
                    border: '1px solid var(--border)',
                    backgroundColor: currentPage === 1 ? 'var(--bg)' : 'var(--primary)',
                    color: currentPage === 1 ? 'var(--text-muted)' : '#fff',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    fontSize: '11px'
                  }}
                >
                  ← Poprz
                </button>
                
                <span style={{ display: 'flex', alignItems: 'center', padding: '0 0.5rem', color: 'var(--text-secondary)' }}>
                  {currentPage}/{totalPages}
                </span>
                
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  style={{
                    padding: '4px 10px',
                    border: '1px solid var(--border)',
                    backgroundColor: currentPage === totalPages ? 'var(--bg)' : 'var(--primary)',
                    color: currentPage === totalPages ? 'var(--text-muted)' : '#fff',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    fontSize: '11px'
                  }}
                >
                  Nast →
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Brak danych */}
      {!loading && reportData.products.length === 0 && (selectedLocations.length > 0 || selectedWarehouses.length > 0) && (
        <div style={{ 
          textAlign: 'center', 
          padding: '1.5rem',
          backgroundColor: 'var(--card-bg)',
          borderRadius: '4px',
          color: 'var(--text-muted)',
          border: '1px solid var(--border)',
          fontSize: '0.8rem'
        }}>
          📋 Brak produktów spełniających kryteria
        </div>
      )}

      {/* Instrukcja */}
      {!loading && selectedLocations.length === 0 && selectedWarehouses.length === 0 && (
        <div style={{ 
          textAlign: 'center', 
          padding: '1.5rem',
          backgroundColor: 'var(--card-bg)',
          borderRadius: '4px',
          border: '1px solid var(--border)'
        }}>
          <h3 style={{ fontSize: '0.9rem', color: 'var(--primary)', marginBottom: '0.5rem' }}>👋 Witaj w Raporcie Magazynowym!</h3>
          <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: 0 }}>Wybierz lokalizacje lub magazyny powyżej, aby wygenerować raport.</p>
        </div>
      )}
    </div>
  );
};

export default InventoryReportPage;
